/*
 * Tab behaviour for sections/lb-product-tabs.liquid.
 *
 * Progressive enhancement: the section ships every panel open under its own heading,
 * which is the layout WooCommerce degraded to without its tabs script. This element
 * only takes over once it upgrades — it hides the panel headings, reveals the tab
 * bar and leaves one panel visible.
 *
 * WooCommerce's tabs were anchor-based (`#tab-description`), so a deep link opened
 * the right tab. That is reproduced from `location.hash` against the panel ids.
 */
class LbProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    /* getElementById rather than a selector: section ids are not CSS-ident safe. */
    this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));

    if (this.tabs.length < 2) return;

    this.classList.add('lb-product-tabs__root--ready');
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);

    const fromHash = this.panels.findIndex((panel) => panel && `#${panel.id}` === window.location.hash);
    this.select(fromHash < 0 ? 0 : fromHash, false);
  }

  onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;

    this.select(this.tabs.indexOf(tab));
  };

  /* Left/right move between tabs, Home/End jump to the ends — the ARIA tabs pattern. */
  onKeyDown = (event) => {
    const current = this.tabs.indexOf(document.activeElement);
    if (current < 0) return;

    const last = this.tabs.length - 1;
    const targets = {
      ArrowLeft: current === 0 ? last : current - 1,
      ArrowRight: current === last ? 0 : current + 1,
      Home: 0,
      End: last,
    };

    if (!(event.key in targets)) return;

    event.preventDefault();
    this.select(targets[event.key]);
  };

  select(index, focus = true) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      /* Only the selected tab stays tabbable; the arrow keys reach the others. */
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      if (this.panels[i]) this.panels[i].hidden = !selected;
    });

    if (focus) this.tabs[index].focus();
  }
}

customElements.define('lb-product-tabs', LbProductTabs);
